import Image from 'next/image'
import Link from 'next/link'
import { FC } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { CONTACT_REQUEST_URL } from '~/core/constants/url'
import { Button } from '~/core/ui/Button'
import { cn } from '~/core/ui/utils'
import StarBadge from './components/StarBadge'
import LPContainer from './LPContainer'

const ApplicantTrackingCRMSimpleEffectiveSection: FC = () => {
  const { t, i18n } = useTranslation()

  return (
    <div className="relative overflow-hidden bg-blue-50 min-h-[834px] tablet:min-h-[768px]">
      {/* Lines on desktop */}
      <div className="z-10 hidden absolute tablet:flex items-center justify-center w-full h-full desktop:bottom-[52rem] tablet:top-0 tablet:right-0">
        <Image
          src="/img/feature/lp/lines_on_hero.svg"
          width={1440}
          height={645}
          alt="lines_on_hero"
          priority
        />
      </div>

      {/* Lines on mobile */}
      <Image
        src="/img/feature/lp/lines_on_hero_left.svg"
        width={160}
        height={200}
        alt="lines_on_hero_left"
        priority
        className="absolute bottom-[271px] -left-0 -top-[6px] tablet:hidden"
      />
      <Image
        src="/img/feature/lp/lines_on_hero_right.svg"
        width={160}
        height={200}
        alt="lines_on_hero_right"
        priority
        className="absolute bottom-[271px] -right-0 -top-[22px] tablet:hidden"
      />

      {/* logos mobile */}
      <Image
        src="/img/feature/applicant-tracking-crm/illus_1.png"
        width={96}
        height={0}
        alt="Illus 1 image"
        className="absolute h-auto tablet:hidden right-[70px] top-[15px]"
      />
      <Image
        src="/img/feature/applicant-tracking-crm/illus_2.png"
        width={70}
        height={0}
        alt="Illus 2 image"
        className="absolute h-auto tablet:hidden left-[70px] top-[75px]"
      />
      <Image
        src="/img/feature/applicant-tracking-crm/illus_3.png"
        width={96}
        height={0}
        alt="Illus 3 image"
        className="absolute h-auto tablet:hidden left-10 bottom-[80px]"
      />
      <Image
        src="/img/feature/applicant-tracking-crm/illus_4.png"
        width={90}
        height={0}
        alt="Illus 4 image"
        className="absolute h-auto tablet:hidden right-[60px] bottom-[60px]"
      />

      {/* logos tablet & desktop */}
      <div
        className={cn(
          'z-20 hidden absolute',
          'tablet:block tablet:top-[399px] tablet:left-0 tablet:w-[100vw]',
          'desktop:left-[calc(50vw_-_617px)] desktop:top-[138px] desktop:w-[1234px]'
        )}>
        <div className="w-fit animate-[fadeInUp_0.2s_linear_0.35s_both] tablet:pl-[7px] desktop:pl-[24px] desktop:pt-[20px]">
          <Image
            src="/img/feature/applicant-tracking-crm/illus_1.png"
            width={155}
            height={0}
            alt="Illus 1 image"
            className="h-auto"
          />
        </div>
        <div className="w-fit animate-[fadeInUp_0.2s_linear_0.35s_both] tablet:pl-[7px] desktop:pl-[104px] desktop:pt-[100px]">
          <Image
            src="/img/feature/applicant-tracking-crm/illus_2.png"
            width={115}
            height={0}
            alt="Illus 2 image"
            className="h-auto"
          />
        </div>
        <div className="absolute -right-[3.6%] top-[30px] animate-[fadeInUp_0.2s_linear_0.35s_both] tablet:right-0 first-letter:">
          <Image
            src="/img/feature/applicant-tracking-crm/illus_3.png"
            width={150}
            height={0}
            alt="Illus 3 image"
          />
        </div>
        <div className="absolute hidden animate-[fadeInUp_0.25s_linear_0.3s_both] tablet:right-[214px] tablet:top-[103px] tablet:inline-block desktop:right-[5.51%] desktop:top-[300px]">
          <Image
            src="/img/feature/applicant-tracking-crm/illus_4.png"
            width={166}
            height={0}
            alt="Illus 4 image"
          />
        </div>
      </div>

      <div className="z-30 absolute top-[167px] tablet:top-44 inset-x-1/8 w-full flex flex-col justify-center">
        <LPContainer>
          <div className="flex justify-center mb-3 tablet:mb-5">
            <StarBadge
              label={t(
                'applicant_tracking_crm:simple_section:take_recruiting_strategy'
              )}
            />
          </div>
          <h1 className="mb-4 animate-fadeInUp text-center font-normal leading-[52px] text-gray-900 text-[43px] tablet:mb-[20] tablet:text-[72px] tablet:leading-[72px]">
            <Trans i18nKey={'applicant_tracking_crm:simple_section:title'}>
              <p className="text-primary-400" />
              <p />
            </Trans>
          </h1>
          <div className="mx-auto max-w-[343px] animate-fadeInUp text-center text-lg tablet:max-w-[654px] text-gray-900 tablet:text-[19px]">
            {t('applicant_tracking_crm:simple_section:description')}
          </div>
          <Link
            href={CONTACT_REQUEST_URL}
            locale={i18n?.language}
            target="_blank">
            <Button
              size="xl"
              label={`${t('applicant_tracking_crm:simple_section:request_demo')}`}
              className="mt-6 mx-auto animate-fadeInUp tablet:mt-[40px]"
              fontWeight="font-normal"
              iconMenus="ArrowUpRight"
              icon="trailing"
            />
          </Link>
        </LPContainer>
      </div>
    </div>
  )
}

export default ApplicantTrackingCRMSimpleEffectiveSection
